import React, { useState } from 'react';
import { User as UserIcon, RotateCcw, Trash2, Search } from 'lucide-react';
import { useQuery, getDeletedUsers, restoreDeletedUser, permanentlyDeleteUser } from 'wasp/client/operations';
import AdminTableLayout from '../../components/AdminTableLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { toast } from '../../../hooks/use-toast';
import useDebounce from '../../../client/hooks/useDebounce';

const UserRecycleBinPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkRestoring, setIsBulkRestoring] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const debouncedSearch = useDebounce(searchTerm, 300);

  const { data, isLoading, refetch } = useQuery(getDeletedUsers, {
    skipPages: currentPage - 1,
    pageSize: itemsPerPage,
    filter: debouncedSearch ? { emailContains: debouncedSearch } : {},
  });

  const users = data?.users ?? [];
  const totalPages = data?.totalPages ?? 1;

  // Selection helpers
  const allIds = users.map((u) => u.id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(allIds));
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRestore = async (userId: string) => {
    try {
      await restoreDeletedUser({ userId });
      await refetch();
      toast({ title: 'User restored successfully' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to restore user', variant: 'destructive' });
    }
  };

  const handlePermanentDelete = async (userId: string) => {
    if (!window.confirm('Permanently delete this user? This cannot be undone.')) return;
    try {
      await permanentlyDeleteUser({ userId });
      await refetch();
      toast({ title: 'User permanently deleted' });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Failed to delete user', variant: 'destructive' });
    }
  };

  const handleBulkRestore = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Restore ${ids.length} user(s)?`)) return;
    setIsBulkRestoring(true);
    try {
      await Promise.all(ids.map((id) => restoreDeletedUser({ userId: id })));
      setSelectedIds(new Set());
      await refetch();
      toast({ title: `${ids.length} user(s) restored` });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Restore failed', variant: 'destructive' });
    } finally {
      setIsBulkRestoring(false);
    }
  };

  const handleBulkPermanentDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} user(s)? This cannot be undone.`)) return;
    setIsBulkDeleting(true);
    try {
      await Promise.all(ids.map((id) => permanentlyDeleteUser({ userId: id })));
      setSelectedIds(new Set());
      await refetch();
      toast({ title: `${ids.length} user(s) permanently deleted` });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message || 'Delete failed', variant: 'destructive' });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  return (
    <div className="w-full">
        {/* Fixed Header */}
        <div className="fixed top-[var(--header-height)] left-[var(--sidebar-width)] right-0 z-30 bg-white dark:bg-sidebar-active transition-colors border-b border-gray-200 dark:border-gray-700">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Recycle Bin</h1>
                <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                  Soft-deleted users. Restore or permanently remove them.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by email…"
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-56"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Spacer */}
        <div className="h-20" />

        {/* Bulk action bar */}
        {someSelected && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg rounded-full px-5 py-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{selectedIds.size} selected</span>
            <Button
              size="sm"
              variant="outline"
              onClick={handleBulkRestore}
              disabled={isBulkRestoring}
              className="rounded-full flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              {isBulkRestoring ? 'Restoring…' : 'Restore'}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleBulkPermanentDelete}
              disabled={isBulkDeleting}
              className="rounded-full flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              {isBulkDeleting ? 'Deleting…' : 'Delete Permanently'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} className="rounded-full">
              Clear
            </Button>
          </div>
        )}

        {/* Table */}
        <div className="w-full">
          <AdminTableLayout
            columns={[]}
            isLoading={isLoading}
            pagination={{
              itemsPerPage,
              onItemsPerPageChange: (value: number) => { setItemsPerPage(value); setCurrentPage(1); },
              currentPage,
              totalPages,
              onPageChange: setCurrentPage,
            }}
            itemsPerPageSelector={
              <Select value={itemsPerPage.toString()} onValueChange={(v) => setItemsPerPage(parseInt(v))}>
                <SelectTrigger className="w-20 h-8 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            }
          >
            {/* Select-all header */}
            {users.length > 0 && (
              <div className="px-6 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 flex items-center gap-3">
                <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} aria-label="Select all" />
                <span className="text-xs text-gray-500">Select all</span>
              </div>
            )}

            {users.length === 0 && !isLoading && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <Trash2 className="w-12 h-12 mb-4 opacity-30" />
                <p className="text-sm">The user recycle bin is empty.</p>
              </div>
            )}

            {users.map((user) => (
              <div
                key={user.id}
                className={`px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center gap-4 ${selectedIds.has(user.id) ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}
              >
                <div onClick={(e) => { e.stopPropagation(); toggleOne(user.id); }}>
                  <Checkbox checked={selectedIds.has(user.id)} onCheckedChange={() => {}} aria-label={`Select ${user.email}`} />
                </div>

                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <UserIcon className="w-4 h-4 text-red-400" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{user.email || '(no email)'}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{user.username || 'No username'}</div>
                </div>

                <div className="hidden md:block text-xs text-gray-500 dark:text-gray-400 w-40 flex-shrink-0">
                  {user.primaryLocation?.name || 'No location'}
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-400 w-36 flex-shrink-0">
                  Deleted{' '}
                  {user.deletedAt
                    ? new Date(user.deletedAt).toLocaleDateString()
                    : '—'}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRestore(user.id)}
                    className="flex items-center gap-1 text-green-700 border-green-300 hover:bg-green-50"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Restore
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePermanentDelete(user.id)}
                    className="flex items-center gap-1 text-red-700 border-red-300 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </AdminTableLayout>
        </div>
      </div>
  );
};

export default UserRecycleBinPage;



